import { isSSR } from 'lib/helpers/isSSR/isSSR'
import { addAction } from 'lib/tracking/datadog'

const SEGMENT_QUEUE_KEY_PREFIX = 'persisted-queue:'
const SEEN_KEY_SUFFIX = ':seen'
const ITEMS_KEY_SUFFIX = ':items'
const SNAPSHOT_KEY_SUFFIXES = [ITEMS_KEY_SUFFIX, SEEN_KEY_SUFFIX]

// Per key, not a namespace budget: five queues × two keys ≈ 750KB of a ~5MB origin quota.
export const MAX_QUEUE_ITEMS_CHARS = 60_000
export const MAX_QUEUE_SEEN_CHARS = 15_000

const getMaxChars = (key: string): number =>
  key.endsWith(SEEN_KEY_SUFFIX) ? MAX_QUEUE_SEEN_CHARS : MAX_QUEUE_ITEMS_CHARS

const isSegmentQueueKey = (key: string): boolean => typeof key === 'string' && key.startsWith(SEGMENT_QUEUE_KEY_PREFIX)

const reportedKeys = new Set<string>()
const droppedSnapshots = new Set<string>()

interface ReportDroppedParams {
  key: string
  chars: number
  reason: 'oversized' | 'quota'
}

const reportDropped = ({ key, chars, reason }: ReportDroppedParams): void => {
  if (reportedKeys.has(key)) {
    return
  }

  reportedKeys.add(key)

  try {
    addAction('segment_queue_write_dropped', { key, chars, reason })
  } catch {
    // Runs inside the SDK's `pagehide` handler; a failure here must not reach it back.
  }
}

const getSnapshotName = (key: string): string | undefined => {
  const suffix = SNAPSHOT_KEY_SUFFIXES.find((candidate) => key.endsWith(candidate))

  return suffix === undefined ? undefined : key.slice(0, -suffix.length)
}

// `:seen` holds the attempt counts for `:items`; keeping one without the other restores those
// events at zero attempts and retries ones that had already exhausted `maxAttempts`.
const dropSnapshot = (key: string): void => {
  const name = getSnapshotName(key)

  if (name === undefined) {
    return
  }

  droppedSnapshots.add(name)

  try {
    SNAPSHOT_KEY_SUFFIXES.forEach((suffix) => window.localStorage.removeItem(`${name}${suffix}`))
  } catch {
    // Best-effort; the next load's sweep clears the leftovers.
  }
}

const sweepOversizedKeys = (): void => {
  try {
    const oversizedKeys = Object.keys(window.localStorage).filter(
      (key) => isSegmentQueueKey(key) && (window.localStorage.getItem(key)?.length ?? 0) > getMaxChars(key),
    )

    oversizedKeys.forEach((key) => window.localStorage.removeItem(key))
  } catch {
    // Storage can be unavailable entirely (privacy mode, blocked cookies).
  }
}

const capFutureWrites = (): void => {
  // The SDK captured `window.localStorage` before this ran, so only a prototype method — resolved at
  // call time — lies in the path of its writes. Untyped callers reach it too, hence the coercion.
  const write = Storage.prototype.setItem

  Storage.prototype.setItem = function setItem(this: Storage, key: string, value: string): void {
    if (!isSegmentQueueKey(key)) {
      write.call(this, key, value)
      return
    }

    const chars = String(value).length
    const snapshotName = getSnapshotName(key)

    if (snapshotName !== undefined && droppedSnapshots.has(snapshotName)) {
      return
    }

    if (chars > getMaxChars(key)) {
      reportDropped({ key, chars, reason: 'oversized' })
      dropSnapshot(key)
      return
    }

    try {
      write.call(this, key, value)
    } catch {
      reportDropped({ key, chars, reason: 'quota' })
      dropSnapshot(key)
    }
  }
}

let isInstalled = false

export const guardSegmentQueueStorage = (): void => {
  if (isSSR() || isInstalled) {
    return
  }

  isInstalled = true

  sweepOversizedKeys()
  capFutureWrites()
}
